import type { PhenomenonDTO } from '@protector/entities/phenomenon/models';
import type { UUID } from '@protector/utils/types';
import type { AxiosObservable } from 'axios-observable';
import axios from 'axios-observable';
import { PROTECTOR_API_URL } from 'config/constants';
import type { PayloadGetStaticPointsSeverityByField, TrapsSeverityByField } from 'pages/static-points/static-points.models';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import StaticPointsUtil from './static-points-util';
import type { StaticPointsDateRange } from './static-points.models';
import type { StaticPointFieldEnum } from '@protector/entities/static-point/constants';
import type { GetStaticPointResponse, StaticPoint, StaticPointCreation } from '@protector/entities/static-point/models';

const protectorApiUrl = PROTECTOR_API_URL;
const staticPointsUrl = `${protectorApiUrl}/v1/timeline/static-points`;
const archeBaseUrl = `${protectorApiUrl}/v1`;
const trapUrl = `${archeBaseUrl}/timeline/traps`;

export const getStaticPoint = (id: UUID, companyId: UUID): Observable<GetStaticPointResponse> => {
  return axios
    .get<GetStaticPointResponse>(`${protectorApiUrl}/api/v1/companies/${companyId}/static-point/${id}`)
    .pipe(map(response => response.data));
};

const getStaticPoints = (
  property_idParam: string,
  fields: StaticPointFieldEnum[],
  dateRange?: StaticPointsDateRange
): Observable<StaticPoint[]> => {
  const params = `property_id=${property_idParam}&fields=${fields.join(',')}`;
  const endDateParam = dateRange?.endDate ? `&end_date=${dateRange.endDate}` : '';
  const dateRangeParams = dateRange ? `&start_date=${dateRange.startDate}${endDateParam}` : '';
  return axios
    .get<StaticPoint[]>(`${staticPointsUrl}?${params}${dateRangeParams}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .pipe(map(response => response.data));
};

const getPhenomenaFromStaticPoints = (staticPoints: StaticPoint[]) => {
  const phenomenaIds: UUID[] = [...new Set(staticPoints.flatMap(StaticPointsUtil.getPhenomenaIdsFromStaticPoint))];
  return axios.post<PhenomenonDTO[]>(`${protectorApiUrl}/api/v1/phenomena/ids`, { ids: phenomenaIds }).pipe(map(r => r.data));
};

const createStaticPoint = (account: { id: UUID; name: string }, staticPoint: StaticPointCreation): AxiosObservable<StaticPointCreation> => {
  return axios.post<StaticPointCreation>(`${trapUrl}`, {
    account,
    payload: staticPoint
  });
};

const getTrapSeverityByField = (payload: PayloadGetStaticPointsSeverityByField): Observable<TrapsSeverityByField> => {
  const url = `${protectorApiUrl}/v1/analytics/traps-severity`;
  return axios.post<TrapsSeverityByField>(url, payload).pipe(map(response => response.data));
};

export const StaticPointService = {
  getStaticPoints,
  getPhenomenaFromStaticPoints,
  getTrapSeverityByField,
  createStaticPoint
};
